import React, { useState, useEffect } from 'react';
import { Drawer, Button, Spin } from 'antd';
import { ChevronLeft, ChevronRight, MessageSquare, Phone, Video, User } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import IMAGES from '../../../../constant/images';
import { rescheduleAppointment } from '../../api/Appointments';

const BookingDialog = ({ open, onClose, counselor, onBookingSuccess, isReschedule = false, existingAppointment = null }) => {
  const dispatch = useDispatch();

  // State management
  const [selectedMode, setSelectedMode] = useState('IN_PERSON');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateOffset, setDateOffset] = useState(0);
  const [availableSlots, setAvailableSlots] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existingAppointment?.mode) {
      const normalizedMode = existingAppointment.mode === 'INPERSON' ? 'IN_PERSON' : existingAppointment.mode;
      setSelectedMode(normalizedMode);
    } else {
      setSelectedMode('IN_PERSON');
    }
  }, [existingAppointment]);

  const modes = [
    { id: 'CHAT', label: 'Chat', icon: MessageSquare },
    { id: 'AUDIO', label: 'Phone Call', icon: Phone },
    { id: 'VIDEO', label: 'Video Call', icon: Video },
    { id: 'IN_PERSON', label: 'In-Person', icon: User },
  ];

  // Fetch available slots when dialog opens
  useEffect(() => {
    if (open && counselor) {
      fetchAvailableSlots();
    }
  }, [open, counselor, currentDate]);

  // Reset selected time when mode or date changes
  useEffect(() => {
    setSelectedTime(null);
  }, [selectedMode, selectedDate]);

  const fetchAvailableSlots = async () => {
    const counsellorId = counselor?.pcUserId || counselor?.counsellorLongId || counselor?.id;

    if (!counsellorId) {
      console.error('No counselor ID found:', counselor);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const today = new Date();
      const isCurrentMonth =
        currentDate.getMonth() === today.getMonth() &&
        currentDate.getFullYear() === today.getFullYear();

      let startDate, endDate;

      if (isCurrentMonth) {
        startDate = formatDateForAPI(today);
        endDate = formatDateForAPI(new Date(today.getFullYear(), today.getMonth() + 1, 0));
      } else {
        startDate = formatDateForAPI(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        endDate = formatDateForAPI(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
      }

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/counsellor/${counsellorId}/available-slot?startDate=${startDate}&endDate=${endDate}`;
      console.log('Fetching available slots from:', apiUrl);
      console.log('Counselor data:', counselor);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('Available slots response:', data);

      if (data?.payload) {
        setAvailableSlots(data.payload);

        if (data.counsellorId && typeof data.counsellorId === 'number') {
          console.log('Extracted numeric counselor ID from slots API:', data.counsellorId);
        }
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to fetch available slots');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate dates for the current month view
  const generateDates = () => {
    const today = new Date();
    const isCurrentMonth =
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();

    const dates = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (isCurrentMonth) {
      for (let i = 0; i < 6; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + dateOffset + i);

        dates.push({
          day: date.getDate(),
          label: dateOffset === 0 && i === 0 ? 'Today' : dayNames[date.getDay()],
          fullDate: date,
          month: date.getMonth(),
          year: date.getFullYear(),
        });
      }

      if (selectedDate === null) {
        setSelectedDate(dates[0].day);
      }
    } else {
      for (let i = 1; i <= 6; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + dateOffset);

        dates.push({
          day: date.getDate(),
          label: dayNames[date.getDay()],
          fullDate: date,
          month: date.getMonth(),
          year: date.getFullYear(),
        });
      }
    }

    return dates;
  };

  const dates = generateDates();

  const getMonthName = () => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const goToPreviousMonth = () => {
    const today = new Date();
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);

    if (
      newDate.getFullYear() >= today.getFullYear() &&
      newDate.getMonth() >= today.getMonth()
    ) {
      setCurrentDate(newDate);
      setSelectedDate(null);
      setDateOffset(0);
    }
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
    setSelectedDate(null);
    setDateOffset(0);
  };

  const goToPreviousDates = () => {
    const today = new Date();
    const isCurrentMonth =
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();

    if (isCurrentMonth && dateOffset > 0) {
      setDateOffset(dateOffset - 6);
      setSelectedDate(null);
    } else if (!isCurrentMonth && dateOffset > 0) {
      setDateOffset(dateOffset - 6);
      setSelectedDate(null);
    }
  };

  const goToNextDates = () => {
    setDateOffset(dateOffset + 6);
    setSelectedDate(null);
  };

  const getTimeSlotsForDate = () => {
    if (!selectedDate) return [];

    const selectedDateObj = dates.find((d) => d.day === selectedDate);
    if (!selectedDateObj) return [];

    const dateKey = formatDateForAPI(selectedDateObj.fullDate);
    const slotsForDate = availableSlots[dateKey] || [];

    const filteredSlots = slotsForDate.filter((slot) => {
      const modes = slot.modes.split(',').map((m) => m.trim());
      return modes.includes(selectedMode) && slot.available;
    });

    const now = new Date();
    const isToday = selectedDateObj.fullDate.toDateString() === now.toDateString();

    if (isToday) {
      const currentTime = now.getHours() * 60 + now.getMinutes();
      return filteredSlots.filter((slot) => {
        const [hours, minutes] = slot.startTime.split(':').map(Number);
        const slotTime = hours * 60 + minutes;
        return slotTime > currentTime;
      });
    }

    return filteredSlots;
  };

  const timeSlots = getTimeSlotsForDate();

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleProceed = async () => {
    if (!selectedMode) {
      toast.error('Please select an appointment mode');
      return;
    }

    if (!selectedDate || !selectedTime) {
      toast.error('Please select both date and time');
      return;
    }

    const selectedDateObj = dates.find((d) => d.day === selectedDate);
    if (!selectedDateObj) {
      toast.error('Invalid date selection');
      return;
    }

    const selectedSlot = timeSlots.find((slot) => slot.startTime === selectedTime);
    if (!selectedSlot) {
      toast.error('Invalid time slot selection');
      return;
    }

    try {
      if (isReschedule && existingAppointment) {
        // Reschedule existing appointment using PATCH
        await dispatch(
          rescheduleAppointment({
            appointmentId: existingAppointment.appointmentId,
            timeSlotRequest: {
              startTime: selectedSlot.startTime,
              endTime: selectedSlot.endTime,
              appointmentDate: formatDateForAPI(selectedDateObj.fullDate),
            },
          })
        ).unwrap();

        handleCancel();

        // Show success dialog after a brief delay to ensure drawer is closed
        setTimeout(() => {
          if (onBookingSuccess) {
            onBookingSuccess(existingAppointment, 'reschedule');
          }
        }, 100);
      } else {
        // Book new appointment using POST
        const token = localStorage.getItem('token');

        const userProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
        const clientId = userProfile.pcUserId || userProfile.id;

        const appointmentData = {
          clientId: clientId,
          counsellorId: counselor.counsellorLongId,
          timeSlotRequest: {
            id: selectedSlot.id || 0,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            appointmentDate: formatDateForAPI(selectedDateObj.fullDate),
          },
          mode: selectedMode === 'IN_PERSON' ? 'INPERSON' : selectedMode,
          anonymous: false,
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/appointment`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(appointmentData),
          }
        );

        const data = await response.json();

        if (response.ok && data) {
          const bookingData = {
            counsellorFirstName: counselor.firstName,
            counsellorLastName: counselor.lastName,
            counsellorTitle: counselor.title,
            date: formatDateForAPI(selectedDateObj.fullDate),
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            mode: selectedMode,
          };

          handleCancel();

          setTimeout(() => {
            if (onBookingSuccess) {
              onBookingSuccess(bookingData, 'booking');
            }
          }, 100);
        } else {
          toast.error(data.message || 'Failed to book appointment');
        }
      }
    } catch (error) {
      console.error('Error with appointment:', error);
      toast.error(isReschedule ? 'Failed to reschedule appointment. Please try again.' : 'Failed to book appointment. Please try again.');
    }
  };

  const handleCancel = () => {
    setSelectedMode('INPERSON');
    setSelectedDate(null);
    setSelectedTime(null);
    setCurrentDate(new Date());
    setDateOffset(0);
    setAvailableSlots({});
    onClose();
  };

  return (
    <Drawer
      title={isReschedule ? "Reschedule Appointment" : "Schedule Appointment"}
      placement="right"
      footer={
        <div className="flex gap-4">
          <button
            onClick={handleCancel}
            className="flex-1 py-3 border-[1px] border-solid border-[#E2E8F0] rounded-xl text-base font-semibold font-poppins text-[#334155] bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleProceed}
            disabled={!selectedMode || !selectedDate || !selectedTime}
            className="flex-1 py-3 btn-gradient border-0 rounded-xl text-base font-semibold font-poppins disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Proceed
          </button>
        </div>
      }
      open={open}
      onClose={handleCancel}
      className="text-[24px] font-poppins text-[#171717]"
      width={450}
      bodyStyle={{ paddingTop: '12px' }}
    >
      <div className="flex flex-col gap-4">
        {/* Counselor Info */}
        <div className="bg-white rounded-2xl p-3 flex flex-col gap-2.5">
          <div className="flex gap-3">
            <div className="w-20 h-20 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-[#F8FAFC] overflow-hidden flex-shrink-0">
              <img
                src={counselor?.imageURL || IMAGES.user_filled}
                alt={`${counselor?.title} ${counselor?.firstName} ${counselor?.lastName}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = IMAGES.user_filled;
                }}
              />
            </div>
            <div className="flex flex-col gap-0.5 flex-1">
              <h3 className="text-base font-semibold text-[#171717] font-poppins mb-0">
                {counselor?.title} {counselor?.firstName} {counselor?.lastName}
              </h3>
              {counselor?.degree && counselor.degree.length > 0 && (
                <p className="text-[10px] text-[#4B61FF] font-poppins font-semibold mb-0">
                  {counselor.degree.join(', ')}
                </p>
              )}
              {counselor?.specializations && counselor.specializations.length > 0 && (
                <p className="text-xs text-[#64748B] font-poppins">
                  {counselor.specializations.join(', ')}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {counselor?.experience && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-[#4B61FF] font-poppins font-semibold">
                  {counselor.experience}+ years exp
                </span>
              </div>
            )}
            {counselor?.rating && (
              <div className="flex items-center gap-1">
                <span className="text-[#F59E0B] text-xs">★</span>
                <span className="text-xs text-[#171717] font-poppins font-medium">
                  {counselor.rating}
                </span>
              </div>
            )}
            {counselor?.languages && counselor.languages.length > 0 && (
              <div className="flex items-center gap-1">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                  <circle cx="8" cy="8" r="6" stroke="#2563EB" strokeWidth="1.2" fill="none"/>
                  <path d="M2 8h12M8 2a9 9 0 0 1 0 12M8 2a9 9 0 0 0 0 12" stroke="#2563EB" strokeWidth="1.2" fill="none"/>
                </svg>
                <span className="text-xs text-[#64748B] font-poppins">
                  {counselor.languages.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="w-full h-px bg-[#E2E8F0]"></div>

        {/* Book Appointment Via */}
        <div className="bg-white rounded-2xl p-3 border-[1px] border-solid border-[#E2E8F0] shadow-sm">
          <h3 className="text-sm font-semibold text-[#171717] mb-2 font-poppins">
            Book Appointment Via
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {modes.map((mode) => {
              const IconComponent = mode.icon;
              const isSelected = selectedMode === mode.id;

              return (
                <button
                  key={mode.id}
                  onClick={() => setSelectedMode(mode.id)}
                  className={`flex flex-col items-center gap-1 py-2 px-0 rounded-xl border-[1px] border-solid transition-all ${
                    isSelected
                      ? 'bg-[#E8F2FF] border-[#B3D4FF]'
                      : 'bg-[#F8FAFC] border-[#E2E8F0] hover:border-[#CBD5E1]'
                  }`}
                >
                  <div className="p-2 rounded-full bg-white">
                    <IconComponent
                      size={16}
                      className={isSelected ? 'text-[#4B61FF]' : 'text-[#64748B]'}
                    />
                  </div>
                  <span
                    className={`text-xs font-poppins ${
                      isSelected ? 'text-[#4B61FF] font-semibold' : 'text-[#334155] font-medium'
                    }`}
                  >
                    {mode.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Choose Date */}
        <div className="bg-white rounded-2xl p-3 border-[1px] border-solid border-[#E2E8F0] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[#171717] font-poppins mb-0">
              Choose Date
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousMonth}
                disabled={
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear()
                }
                className="flex items-center justify-center w-[26px] h-[26px] p-0 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0 disabled:opacity-30"
              >
                <ChevronLeft size={14} className="text-[#64748B]" />
              </button>
              <span className="text-sm font-semibold text-[#171717] font-poppins min-w-[80px] text-center">
                {getMonthName()}
              </span>
              <button onClick={goToNextMonth} className="flex items-center justify-center w-[26px] h-[26px] p-0 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0">
                <ChevronRight size={14} className="text-[#64748B]" />
              </button>
            </div>
          </div>

          {/* Date block navigation (for navigating through 6-date blocks within month) */}
          <div className="flex items-center justify-between mt-2 mb-2">
            <button
              onClick={goToPreviousDates}
              disabled={dateOffset === 0}
              className="p-1 bg-transparent border-none disabled:opacity-30 hover:bg-[#F8FAFC] rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-[#2563EB]" />
            </button>
            <button
              onClick={goToNextDates}
              className="p-1 bg-transparent border-none hover:bg-[#F8FAFC] rounded-lg transition-colors"
            >
              <ChevronRight size={20} className="text-[#2563EB]" />
            </button>
          </div>

          <div className="grid grid-cols-6 gap-2">
            {dates.map((date) => {
              const isSelected = selectedDate === date.day;

              return (
                <button
                  key={`${date.day}-${date.month}`}
                  onClick={() => setSelectedDate(date.day)}
                  className={`flex flex-col items-center py-1.5 rounded-xl border-0 transition-all ${
                    isSelected ? 'bg-[#2563EB]' : 'bg-[#EFF6FF] hover:bg-[#DBEAFE]'
                  }`}
                >
                  <div
                    className="flex items-center justify-center w-7 h-7 rounded-full"
                  >
                    <span
                      className={`text-sm font-semibold font-poppins ${
                        isSelected ? 'text-white' : 'text-[#171717]'
                      }`}
                    >
                      {date.day}
                    </span>
                  </div>
                  <span
                    className={`text-[11px] font-medium font-poppins ${
                      isSelected ? 'text-white' : 'text-[#64748B]'
                    }`}
                  >
                    {date.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Choose Time */}
        <div className="bg-white rounded-2xl p-3 border-[1px] border-solid border-[#E2E8F0] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[#171717] font-poppins mb-0">
              Choose Time
            </h3>
            <span className="text-[11px] font-semibold text-[#2563EB] bg-[#EFF6FF] px-2 py-0.5 rounded-full font-poppins">
              {timeSlots.length} Slots
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-4">
              <Spin size="large" />
            </div>
          ) : timeSlots.length === 0 ? (
            <div className="text-center text-[#64748B] font-poppins py-4 text-sm">
              {selectedDate ? 'No available slots for this date and mode' : 'Please select a date'}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {timeSlots.map((slot) => {
                const isSelected = selectedTime === slot.startTime;

                return (
                  <Button
                    key={slot.startTime}
                    onClick={() => setSelectedTime(slot.startTime)}
                    className={`text-[13px] font-semibold border-0 font-poppins h-[36px] rounded-xl ${
                      isSelected
                        ? 'bg-[#2563EB] text-[#FFFFFF]'
                        : 'bg-[#EFF6FF] text-[#334155]'
                    }`}
                  >
                    {formatTime(slot.startTime)}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default BookingDialog;
